import {
  useContext,
  useEffect,
  useState,
  useRef,
  useCallback,
  ReactNode,
} from "react";
import { useLocation, useHistory } from "react-router-dom";
import {
  Avatar,
  Badge,
  Button,
  Dropdown,
  Menu,
  message,
  Drawer,
  Divider,
} from "antd";
import {
  UploadOutlined,
  MenuOutlined,
  CloseOutlined,
  BellOutlined,
  CommentOutlined,
  QuestionCircleOutlined,
  DownOutlined,
  DashboardOutlined,
  SearchOutlined,
  EyeOutlined,
  VideoCameraOutlined,
  PrinterOutlined,
  CalendarOutlined,
  TeamOutlined,
  IdcardOutlined,
  PictureOutlined,
  ProjectOutlined,
  CustomerServiceOutlined,
  BarChartOutlined,
  LayoutOutlined,
  DownloadOutlined,
  UserOutlined,
  LineChartOutlined,
  HomeOutlined,
  DatabaseOutlined,
  FolderOpenOutlined,
  CameraOutlined,
  PlayCircleOutlined,
} from "@ant-design/icons";
import { format } from "date-fns";
import { observer } from "mobx-react";
import { COLORS } from "../../styled/basic";
import Logo from "../../assets/images/MomentoHeader.png";
import { MomentServiceInstance } from "../../services/moments";
import { NotificationServiceInstance } from "../../services/notification";
import { Routes } from "../../utils/routes";
import {
  StyledLink,
  NavigationBlock,
  NavigationItem,
  AvatarBlock,
  AvatarArea,
  DesktopNavGroup,
  DesktopAvatarWrap,
  MobileMenuWrap,
  HamburgerButton,
  MobileDrawerLink,
  MobileDrawerMuted,
  NotificationBellButton,
  NavDropdownTrigger,
  NavDropdownMenu,
  NavDropdownItemRow,
  NavDropdownItemIcon,
  NavDropdownItemLabel,
  MobileDrawerLinkWithIcon,
} from "./styled";
import {
  SessionContext,
  SessionServiceInstance,
} from "../../services/session";
import { CognitoHostedUILogoutURL } from "../../pages/public/login";
import {
  getMyAccess,
  getOperatorAvatarUploadUrl,
  updateOperatorAvatar,
  notifyOperatorGameDayLogin,
  notifyOperatorFirstLogin,
} from "../../axios/routes/access";
import { OperatorNotificationFeedInstance } from "../../services/operatorNotificationFeed";
import { normalizeOperatorEmail } from "../../utils/operatorEmail";
import { getAdminChatUnreadCount } from "../../axios/routes/operatorAdminChat";
import { OperatorAdminChatDrawer } from "../OperatorAdminChatDrawer";
import { OperatorHelpDrawer } from "../OperatorHelpDrawer";
import { primeOperatorAdminChatAudioOnUserGesture } from "../../utils/operatorAdminChatSound";

type NavDropdownItem = {
  label: string;
  route: string;
  icon: ReactNode;
};

const OPERATIONS_NAV: NavDropdownItem[] = [
  { label: "Live Game View", route: Routes.LIVE_GAME_VIEW, icon: <PlayCircleOutlined /> },
  { label: "Monitoring", route: Routes.MONITORING, icon: <DashboardOutlined /> },
  { label: "Search a Seat", route: Routes.SEARCH_A_SEAT, icon: <SearchOutlined /> },
  { label: "Moment Viewer", route: Routes.VISION, icon: <EyeOutlined /> },
  { label: "Venue Vision", route: Routes.CROWD_ANALYTICS, icon: <LineChartOutlined /> },
  { label: "Venue Cameras", route: Routes.TRIGGERING, icon: <VideoCameraOutlined /> },
  { label: "CX Prints", route: Routes.PRINTS, icon: <PrinterOutlined /> },
];

const EMPLOYEES_NAV: NavDropdownItem[] = [
  { label: "Scheduling", route: Routes.SCHEDULING, icon: <CalendarOutlined /> },
  { label: "Manage Operators", route: Routes.OPERATORS, icon: <TeamOutlined /> },
  { label: "Payroll", route: Routes.EMPLOYEE_MANAGEMENT, icon: <IdcardOutlined /> },
];

const CLIENT_SERVICES_NAV: NavDropdownItem[] = [
  { label: "Client Tracker", route: Routes.CLIENT_TRACKER, icon: <ProjectOutlined /> },
  { label: "Seat View", route: Routes.SEAT_VIEW, icon: <PictureOutlined /> },
  { label: "Support", route: Routes.SUPPORT, icon: <CustomerServiceOutlined /> },
  { label: "Engagement", route: Routes.ENGAGEMENT, icon: <BarChartOutlined /> },
  { label: "Templates", route: Routes.TEMPLATES, icon: <LayoutOutlined /> },
  { label: "Downloads", route: Routes.DOWNLOADS, icon: <DownloadOutlined /> },
  { label: "Profiles", route: Routes.PROFILES, icon: <UserOutlined /> },
];

const STADIUM_SETUP_NAV: NavDropdownItem[] = [
  { label: "Stadium", route: Routes.STADIUM, icon: <HomeOutlined /> },
  { label: "Indexing", route: Routes.INDEXING, icon: <DatabaseOutlined /> },
];

function routeGroupActive(pathname: string, routes: string[]): boolean {
  return routes.some((route) => pathname === route || pathname.startsWith(`${route}/`));
}

function NavHoverDropdown({
  label,
  active,
  items,
  onNavigate,
}: {
  label: string;
  active: boolean;
  items: NavDropdownItem[];
  onNavigate: (route: string) => void;
}): JSX.Element {
  const menu = (
    <NavDropdownMenu>
      {items.map((item) => (
        <Menu.Item key={item.route} onClick={() => onNavigate(item.route)}>
          <NavDropdownItemRow>
            <NavDropdownItemIcon>{item.icon}</NavDropdownItemIcon>
            <NavDropdownItemLabel>{item.label}</NavDropdownItemLabel>
          </NavDropdownItemRow>
        </Menu.Item>
      ))}
    </NavDropdownMenu>
  );

  return (
    <Dropdown overlay={menu} trigger={["hover"]} placement="bottomLeft">
      <NavDropdownTrigger>
        <NavigationItem active={active}>
          {label}
          {" "}
          <DownOutlined style={{ fontSize: 9, marginLeft: 2 }} />
        </NavigationItem>
      </NavDropdownTrigger>
    </Dropdown>
  );
}

function MobileNavSection({
  title,
  items,
  pathname,
  onNavigate,
}: {
  title: string;
  items: NavDropdownItem[];
  pathname: string;
  onNavigate: (route: string) => void;
}): JSX.Element {
  return (
    <>
      <MobileDrawerMuted style={{ paddingTop: 14, fontWeight: 600, color: COLORS.BLACK_MAIN }}>
        {title}
      </MobileDrawerMuted>
      {items.map((item) => (
        <MobileDrawerLinkWithIcon
          key={item.route}
          type="button"
          $active={pathname === item.route || pathname.startsWith(`${item.route}/`)}
          onClick={() => onNavigate(item.route)}
        >
          <span aria-hidden>{item.icon}</span>
          <span>{item.label}</span>
        </MobileDrawerLinkWithIcon>
      ))}
    </>
  );
}

export const NavigationComponent = observer((): JSX.Element => {
  const { pathname } = useLocation();
  const history = useHistory();
  const { EmailAddress } = useContext(SessionContext);
  const {
    isSuperAdmin,
    isOwnerAdmin,
    isIndexingOperator,
    isIndexingOnlyOperator,
    isTagboardOnlyOperator,
    canViewScheduleNav,
  } = SessionServiceInstance;
  const showIndexingOnlyNav = isIndexingOnlyOperator;
  const showTagboardOnlyNav = isTagboardOnlyOperator;
  const showHybridIndexingNav = isIndexingOperator && !isSuperAdmin && !isIndexingOnlyOperator;
  const [avatarUrl, setAvatarUrl] = useState<string | undefined>();
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [adminChatOpen, setAdminChatOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [adminChatFocusKey, setAdminChatFocusKey] = useState<string | null>(null);
  const [adminChatOperatorGameId, setAdminChatOperatorGameId] = useState<string | null>(null);
  const [adminChatShowStartChatCta, setAdminChatShowStartChatCta] = useState(false);
  const [chatUnread, setChatUnread] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const closeMobileMenu = () => setMobileMenuOpen(false);

  const resetMomentPage = () => {
    MomentServiceInstance.clearData();
    NotificationServiceInstance.closeUpdatedMomentNotification();
  };

  const navigateMobile = (path: string) => {
    history.push(path);
    resetMomentPage();
    closeMobileMenu();
  };

  const refetchAvatar = useCallback(() => {
    if (!EmailAddress) {
      setAvatarUrl(undefined);
      return;
    }
    getMyAccess(EmailAddress)
      .then((access) => {
        setAvatarUrl(access.avatarUrl);
      })
      .catch(() => { /* ignore */ });
  }, [EmailAddress]);

  useEffect(() => {
    refetchAvatar();
  }, [EmailAddress, pathname, refetchAvatar]);

  useEffect(() => {
    if (!EmailAddress) {
      OperatorNotificationFeedInstance.setActiveViewer(null);
      return undefined;
    }
    const e = normalizeOperatorEmail(EmailAddress);
    OperatorNotificationFeedInstance.setActiveViewer(e);
    OperatorNotificationFeedInstance.refreshSnapshot(EmailAddress).catch(() => undefined);
    OperatorNotificationFeedInstance.startNotificationPolling(EmailAddress);

    const gameDayLoginKey = `momento-game-day-login-notify:${e}`;
    if (!sessionStorage.getItem(gameDayLoginKey)) {
      sessionStorage.setItem(gameDayLoginKey, "1");
      notifyOperatorGameDayLogin(e).catch(() => undefined);
    }

    const firstLoginKey = `momento-first-login-notify:${e}`;
    if (!sessionStorage.getItem(firstLoginKey)) {
      sessionStorage.setItem(firstLoginKey, "1");
      notifyOperatorFirstLogin(e).catch(() => undefined);
    }

    return () => {
      OperatorNotificationFeedInstance.stopNotificationPolling();
    };
  }, [EmailAddress]);

  useEffect(() => {
    const onOpenChat = (ev: Event) => {
      primeOperatorAdminChatAudioOnUserGesture();
      const { detail } = ev as CustomEvent<{
        threadKey?: string;
        gameIdForOperatorContext?: string | null;
        showStartChatCta?: boolean;
      }>;
      setAdminChatFocusKey(detail?.threadKey || null);
      setAdminChatOperatorGameId(
        typeof detail?.gameIdForOperatorContext === "string"
          ? detail.gameIdForOperatorContext
          : null,
      );
      setAdminChatShowStartChatCta(!!detail?.showStartChatCta);
      setAdminChatOpen(true);
    };
    window.addEventListener("momento:open-admin-chat", onOpenChat);
    return () => window.removeEventListener("momento:open-admin-chat", onOpenChat);
  }, []);

  useEffect(() => {
    if (!EmailAddress) {
      setChatUnread(0);
      return undefined;
    }
    const tick = () => {
      getAdminChatUnreadCount(EmailAddress).then(setChatUnread).catch(() => undefined);
    };
    tick();
    const id = window.setInterval(tick, 5_000);
    return () => window.clearInterval(id);
  }, [EmailAddress]);

  useEffect(() => {
    const onProfileUpdated = () => {
      refetchAvatar();
      SessionServiceInstance.refreshProfileCompleteness().catch(() => undefined);
    };
    window.addEventListener("momento:operator-profile-updated", onProfileUpdated);
    return () => {
      window.removeEventListener("momento:operator-profile-updated", onProfileUpdated);
    };
  }, [refetchAvatar]);

  const onLogout = async () => {
    console.log("on logout");
    const emailKey = normalizeOperatorEmail(EmailAddress || "");
    await SessionServiceInstance.clearSession();
    try {
      if (emailKey) {
        sessionStorage.removeItem(`momento-game-day-login-notify:${emailKey}`);
        sessionStorage.removeItem(`momento-first-login-notify:${emailKey}`);
      }
    } catch {
      /* ignore */
    }
    window.location.href = CognitoHostedUILogoutURL();
  };

  const onUploadPhotoClick = () => {
    fileInputRef.current?.click();
  };

  const onMobileUploadPhoto = () => {
    closeMobileMenu();
    window.setTimeout(() => fileInputRef.current?.click(), 0);
  };

  const onMobileLogout = async () => {
    closeMobileMenu();
    await onLogout();
  };

  const handleAvatarUpload = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = e.target.files?.[0];
    if (!file || !file.type.startsWith("image/") || !EmailAddress) return;

    try {
      setAvatarUploading(true);
      const contentType = file.type || "image/jpeg";
      const { url, fields, s3Key } = await getOperatorAvatarUploadUrl(
        EmailAddress,
        contentType,
      );
      const formData = new FormData();
      Object.entries(fields).forEach(([k, v]) => formData.append(k, v));
      formData.append("file", file);
      await fetch(url, { method: "POST", body: formData });
      await updateOperatorAvatar(EmailAddress, s3Key);
      const access = await getMyAccess(EmailAddress);
      setAvatarUrl(access.avatarUrl);
      await SessionServiceInstance.refreshProfileCompleteness();
      message.success("Profile photo updated.");
    } catch (err) {
      message.error(err?.message || "Failed to upload photo.");
    } finally {
      setAvatarUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const feed = OperatorNotificationFeedInstance;
  const notificationPreview = feed.mergedItems.slice(0, 8);

  const notificationPanel = (
    <div
      style={{
        width: 340,
        maxHeight: 420,
        overflowY: "auto",
        background: "#fff",
        borderRadius: 8,
        boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
      }}
    >
      <div style={{
        padding: "10px 16px",
        fontWeight: 600,
        fontFamily: "\"Montserrat\", sans-serif",
        borderBottom: "1px solid #f0f0f0",
      }}
      >
        Notifications
      </div>
      {notificationPreview.length === 0 ? (
        <div style={{
          padding: 24,
          color: "#888",
          textAlign: "center",
          fontFamily: "\"Montserrat\", sans-serif",
          fontSize: 13,
        }}
        >
          No notifications yet
        </div>
      ) : (
        notificationPreview.map((row) => (
          <button
            key={row.id}
            type="button"
            onClick={() => {
              if (row.adminChatThreadKey) {
                window.dispatchEvent(
                  new CustomEvent("momento:open-admin-chat", {
                    detail: { threadKey: row.adminChatThreadKey },
                  }),
                );
                return;
              }
              if (row.href) {
                history.push(row.href);
              }
            }}
            style={{
              display: "block",
              width: "100%",
              textAlign: "left",
              padding: "10px 16px",
              border: "none",
              borderBottom: "1px solid #f5f5f5",
              background: "#fff",
              cursor: row.href || row.adminChatThreadKey ? "pointer" : "default",
              fontFamily: "\"Montserrat\", sans-serif",
              fontSize: 13,
            }}
          >
            <div style={{ fontWeight: 600, marginBottom: 4 }}>{row.title}</div>
            {row.description ? (
              <div style={{ color: "rgba(0,0,0,0.65)", lineHeight: 1.4 }}>
                {row.description}
              </div>
            ) : null}
            <div style={{ color: "#999", fontSize: 11, marginTop: 6 }}>
              {format(row.atMs, "MMM d, h:mm a")}
            </div>
          </button>
        ))
      )}
      <div style={{ padding: 8, borderTop: "1px solid #f0f0f0" }}>
        <Button
          type="link"
          block
          onClick={() => history.push(Routes.NOTIFICATIONS)}
          style={{ fontFamily: "\"Montserrat\", sans-serif" }}
        >
          View all notifications
        </Button>
      </div>
    </div>
  );

  const menu = (
    <Menu>
      <Menu.Item
        onClick={() => history.push(Routes.ACCOUNT)}
      >
        Account Information
      </Menu.Item>
      <Menu.Item
        onClick={() => {
          feed.markNotificationsSeen();
          history.push(Routes.NOTIFICATIONS);
        }}
        icon={<BellOutlined />}
      >
        Notifications
      </Menu.Item>
      {isOwnerAdmin && (
        <Menu.Item
          onClick={() => history.push(Routes.LOGGING)}
        >
          Logging
        </Menu.Item>
      )}
      {isOwnerAdmin && (
        <Menu.Item
          onClick={() => history.push(Routes.LIVE_MOMENTS_CLEANUP)}
        >
          Live Moments Cleanup
        </Menu.Item>
      )}
      {isSuperAdmin && (
        <Menu.Item
          onClick={() => history.push(Routes.MANAGE_ACCESS)}
        >
          Manage Access
        </Menu.Item>
      )}
      <Menu.Item
        onClick={onUploadPhotoClick}
        disabled={avatarUploading}
        icon={<UploadOutlined />}
      >
        {avatarUploading ? "Uploading..." : "Upload photo"}
      </Menu.Item>
      <Menu.Item onClick={onLogout}>
        Logout
      </Menu.Item>
    </Menu>
  );

  const pathActive = (routePrefix: string) => pathname.includes(routePrefix);

  const navigateDesktop = (path: string) => {
    history.push(path);
    resetMomentPage();
  };

  const operationsActive = routeGroupActive(pathname, OPERATIONS_NAV.map((i) => i.route));
  const employeesActive = routeGroupActive(pathname, EMPLOYEES_NAV.map((i) => i.route));
  const clientServicesActive = routeGroupActive(pathname, CLIENT_SERVICES_NAV.map((i) => i.route));
  const stadiumSetupActive = routeGroupActive(pathname, STADIUM_SETUP_NAV.map((i) => i.route));
  const snapsActive = pathActive(Routes.SNAPS);
  const filesActive = pathActive(Routes.FILES);

  return (
    <>
      <NavigationBlock>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          style={{ display: "none" }}
          onChange={handleAvatarUpload}
        />

        <div style={{
          display: "flex",
          alignItems: "center",
          height: "100%",
          marginRight: 16,
          flexShrink: 0,
        }}
        >
          <button
            type="button"
            aria-label="Home — Games"
            onClick={() => {
              history.push(Routes.GAMES);
              resetMomentPage();
              closeMobileMenu();
            }}
            style={{
              padding: 0,
              margin: 0,
              border: "none",
              background: "none",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
            }}
          >
            <img
              src={Logo}
              alt="Company Logo"
              style={{ height: 34, display: "block" }}
            />
          </button>
        </div>

        <DesktopNavGroup>
          {showTagboardOnlyNav ? (
            <StyledLink to={Routes.DOWNLOADS} onClick={resetMomentPage}>
              <NavigationItem active={pathActive(Routes.DOWNLOADS)}>
                Videoboard
              </NavigationItem>
            </StyledLink>
          ) : (
            <>
              {!showIndexingOnlyNav && (
                <StyledLink to={Routes.GAMES} onClick={resetMomentPage}>
                  <NavigationItem active={pathActive(Routes.GAMES)}>
                    Games
                  </NavigationItem>
                </StyledLink>
              )}

              {isSuperAdmin && !showIndexingOnlyNav && (
                <StyledLink to={Routes.TEAMS} onClick={resetMomentPage}>
                  <NavigationItem active={pathActive(Routes.TEAMS)}>
                    Teams
                  </NavigationItem>
                </StyledLink>
              )}

              {isSuperAdmin && !showIndexingOnlyNav ? (
                <>
                  <NavHoverDropdown
                    label="Operations"
                    active={operationsActive}
                    items={OPERATIONS_NAV}
                    onNavigate={navigateDesktop}
                  />
                  <NavHoverDropdown
                    label="Employees"
                    active={employeesActive}
                    items={EMPLOYEES_NAV}
                    onNavigate={navigateDesktop}
                  />
                  <NavHoverDropdown
                    label="Client Services"
                    active={clientServicesActive}
                    items={CLIENT_SERVICES_NAV}
                    onNavigate={navigateDesktop}
                  />
                  <StyledLink to={Routes.SNAPS} onClick={resetMomentPage}>
                    <NavigationItem active={snapsActive}>
                      Snaps
                    </NavigationItem>
                  </StyledLink>
                  <NavHoverDropdown
                    label="Stadium Setup"
                    active={stadiumSetupActive}
                    items={STADIUM_SETUP_NAV}
                    onNavigate={navigateDesktop}
                  />
                  <StyledLink to={Routes.FILES} onClick={resetMomentPage}>
                    <NavigationItem active={filesActive}>
                      Files
                    </NavigationItem>
                  </StyledLink>
                </>
              ) : showIndexingOnlyNav ? (
                <NavHoverDropdown
                  label="Stadium Setup"
                  active={stadiumSetupActive}
                  items={STADIUM_SETUP_NAV}
                  onNavigate={navigateDesktop}
                />
              ) : (
                <>
                  {canViewScheduleNav && (
                    <StyledLink to={Routes.SCHEDULE} onClick={resetMomentPage}>
                      <NavigationItem active={pathActive(Routes.SCHEDULE)}>
                        Schedule
                      </NavigationItem>
                    </StyledLink>
                  )}
                  {showHybridIndexingNav && (
                    <NavHoverDropdown
                      label="Stadium Setup"
                      active={stadiumSetupActive}
                      items={STADIUM_SETUP_NAV}
                      onNavigate={navigateDesktop}
                    />
                  )}
                </>
              )}
            </>
          )}
        </DesktopNavGroup>

        <DesktopAvatarWrap>
          <AvatarBlock>
            <Dropdown
              overlay={notificationPanel}
              trigger={["click"]}
              placement="bottomRight"
              onVisibleChange={(open) => {
                if (open) {
                  feed.markNotificationsSeen();
                }
              }}
            >
              <NotificationBellButton type="button" aria-label="Open notifications">
                <Badge
                  count={feed.unreadCount}
                  size="small"
                  overflowCount={99}
                  style={{
                    backgroundColor: COLORS.BLUE_MAIN,
                    color: "#fff",
                  }}
                >
                  <BellOutlined
                    style={{
                      fontSize: 20,
                      color: "rgba(255, 255, 255, 0.92)",
                      display: "block",
                    }}
                  />
                </Badge>
              </NotificationBellButton>
            </Dropdown>
            {isSuperAdmin && (
              <NotificationBellButton
                type="button"
                aria-label="Open help"
                onClick={() => setHelpOpen(true)}
              >
                <QuestionCircleOutlined
                  style={{
                    fontSize: 20,
                    color: "rgba(255, 255, 255, 0.92)",
                    display: "block",
                  }}
                />
              </NotificationBellButton>
            )}
            {!showIndexingOnlyNav && !showTagboardOnlyNav && (
            <NotificationBellButton
              type="button"
              aria-label="Open admin chat with super admins"
              onClick={() => {
                primeOperatorAdminChatAudioOnUserGesture();
                setAdminChatFocusKey(null);
                setAdminChatShowStartChatCta(false);
                setAdminChatOpen(true);
              }}
              style={{ marginRight: 8 }}
            >
              <Badge
                count={chatUnread}
                size="small"
                overflowCount={99}
                style={{
                  backgroundColor: "#0d9488",
                  color: "#fff",
                }}
              >
                <CommentOutlined
                  style={{
                    fontSize: 20,
                    color: "rgba(255, 255, 255, 0.92)",
                    display: "block",
                  }}
                />
              </Badge>
            </NotificationBellButton>
            )}
            <Dropdown overlay={menu} placement="bottomRight">
              <AvatarArea>
                <Avatar
                  key={avatarUrl || "no-avatar"}
                  alt="User Avatar"
                  src={avatarUrl}
                />
              </AvatarArea>
            </Dropdown>
          </AvatarBlock>
        </DesktopAvatarWrap>

        <MobileMenuWrap>
          <HamburgerButton
            type="button"
            aria-label="Open menu"
            aria-expanded={mobileMenuOpen}
            onClick={() => setMobileMenuOpen(true)}
          >
            <MenuOutlined />
          </HamburgerButton>
        </MobileMenuWrap>
      </NavigationBlock>

      <Drawer
        title={(
          <span style={{
            color: "#fff",
            fontFamily: "'Montserrat', sans-serif",
            fontWeight: 600,
          }}
          >
            Menu
          </span>
        )}
        closeIcon={<CloseOutlined style={{ color: "#fff", fontSize: 16 }} />}
        placement="left"
        width={300}
        visible={mobileMenuOpen}
        onClose={closeMobileMenu}
        headerStyle={{
          background: COLORS.BLACK_MAIN,
          borderBottom: `2px solid ${COLORS.BLUE_MAIN}`,
        }}
        bodyStyle={{ padding: 0 }}
      >
        <nav aria-label="Primary">
          {showTagboardOnlyNav ? (
            <MobileDrawerLink
              type="button"
              $active={pathActive(Routes.DOWNLOADS)}
              onClick={() => navigateMobile(Routes.DOWNLOADS)}
            >
              Videoboard
            </MobileDrawerLink>
          ) : (
            <>
              {!showIndexingOnlyNav && (
                <MobileDrawerLink
                  type="button"
                  $active={pathActive(Routes.GAMES)}
                  onClick={() => navigateMobile(Routes.GAMES)}
                >
                  Games
                </MobileDrawerLink>
              )}

              {isSuperAdmin && !showIndexingOnlyNav && (
                <MobileDrawerLink
                  type="button"
                  $active={pathActive(Routes.TEAMS)}
                  onClick={() => navigateMobile(Routes.TEAMS)}
                >
                  Teams
                </MobileDrawerLink>
              )}

              {isSuperAdmin && !showIndexingOnlyNav ? (
                <>
                  <MobileNavSection
                    title="Operations"
                    items={OPERATIONS_NAV}
                    pathname={pathname}
                    onNavigate={navigateMobile}
                  />
                  <MobileNavSection
                    title="Employees"
                    items={EMPLOYEES_NAV}
                    pathname={pathname}
                    onNavigate={navigateMobile}
                  />
                  <MobileNavSection
                    title="Client Services"
                    items={CLIENT_SERVICES_NAV}
                    pathname={pathname}
                    onNavigate={navigateMobile}
                  />
                  <MobileDrawerLinkWithIcon
                    type="button"
                    $active={snapsActive}
                    onClick={() => navigateMobile(Routes.SNAPS)}
                  >
                    <span aria-hidden><CameraOutlined /></span>
                    <span>Snaps</span>
                  </MobileDrawerLinkWithIcon>
                  <MobileNavSection
                    title="Stadium Setup"
                    items={STADIUM_SETUP_NAV}
                    pathname={pathname}
                    onNavigate={navigateMobile}
                  />
                  <MobileDrawerLinkWithIcon
                    type="button"
                    $active={filesActive}
                    onClick={() => navigateMobile(Routes.FILES)}
                  >
                    <span aria-hidden><FolderOpenOutlined /></span>
                    <span>Files</span>
                  </MobileDrawerLinkWithIcon>
                </>
              ) : showIndexingOnlyNav ? (
                <MobileNavSection
                  title="Stadium Setup"
                  items={STADIUM_SETUP_NAV}
                  pathname={pathname}
                  onNavigate={navigateMobile}
                />
              ) : (
                <>
                  {canViewScheduleNav && (
                    <MobileDrawerLink
                      type="button"
                      $active={pathActive(Routes.SCHEDULE)}
                      onClick={() => navigateMobile(Routes.SCHEDULE)}
                    >
                      Schedule
                    </MobileDrawerLink>
                  )}
                  {showHybridIndexingNav && (
                    <MobileNavSection
                      title="Stadium Setup"
                      items={STADIUM_SETUP_NAV}
                      pathname={pathname}
                      onNavigate={navigateMobile}
                    />
                  )}
                </>
              )}
            </>
          )}
        </nav>

        <Divider style={{ margin: 0 }} />

        <nav aria-label="Account">
          <MobileDrawerMuted>
            Signed in as
          </MobileDrawerMuted>
          <MobileDrawerMuted style={{
            paddingTop: 0,
            fontWeight: 600,
            color: COLORS.BLACK_MAIN,
          }}
          >
            {EmailAddress || "—"}
          </MobileDrawerMuted>

          <MobileDrawerLink
            type="button"
            $active={pathActive(Routes.ACCOUNT)}
            onClick={() => navigateMobile(Routes.ACCOUNT)}
          >
            Account Information
          </MobileDrawerLink>

          {!showIndexingOnlyNav && !showTagboardOnlyNav && (
          <MobileDrawerLink
            type="button"
            $active={false}
            onClick={() => {
              primeOperatorAdminChatAudioOnUserGesture();
              closeMobileMenu();
              setAdminChatFocusKey(null);
              setAdminChatShowStartChatCta(false);
              setAdminChatOpen(true);
            }}
          >
            <CommentOutlined style={{ marginRight: 8 }} />
            Admin chat
            {chatUnread > 0 ? (
              <span
                style={{
                  marginLeft: 8,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minWidth: 22,
                  height: 22,
                  padding: "0 6px",
                  borderRadius: 11,
                  background: "#0d9488",
                  color: "#fff",
                  fontSize: 11,
                  fontWeight: 700,
                  fontFamily: "\"Montserrat\", sans-serif",
                }}
              >
                {chatUnread > 99 ? "99+" : chatUnread}
              </span>
            ) : null}
          </MobileDrawerLink>
          )}

          {isSuperAdmin && (
          <MobileDrawerLink
            type="button"
            $active={false}
            onClick={() => {
              closeMobileMenu();
              setHelpOpen(true);
            }}
          >
            <QuestionCircleOutlined style={{ marginRight: 8 }} />
            Help
          </MobileDrawerLink>
          )}

          <MobileDrawerLink
            type="button"
            $active={pathActive(Routes.NOTIFICATIONS)}
            onClick={() => {
              OperatorNotificationFeedInstance.markNotificationsSeen();
              navigateMobile(Routes.NOTIFICATIONS);
            }}
          >
            <BellOutlined style={{ marginRight: 8 }} />
            Notifications
            {feed.unreadCount > 0 ? (
              <span
                style={{
                  marginLeft: 8,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minWidth: 22,
                  height: 22,
                  padding: "0 6px",
                  borderRadius: 11,
                  background: COLORS.BLUE_MAIN,
                  color: "#fff",
                  fontSize: 11,
                  fontWeight: 700,
                  fontFamily: "\"Montserrat\", sans-serif",
                }}
              >
                {feed.unreadCount > 99 ? "99+" : feed.unreadCount}
              </span>
            ) : null}
          </MobileDrawerLink>

          {isOwnerAdmin && (
            <MobileDrawerLink
              type="button"
              $active={pathActive(Routes.LOGGING)}
              onClick={() => navigateMobile(Routes.LOGGING)}
            >
              Logging
            </MobileDrawerLink>
          )}
          {isOwnerAdmin && (
            <MobileDrawerLink
              type="button"
              $active={pathActive(Routes.LIVE_MOMENTS_CLEANUP)}
              onClick={() => navigateMobile(Routes.LIVE_MOMENTS_CLEANUP)}
            >
              Live Moments Cleanup
            </MobileDrawerLink>
          )}

          {isSuperAdmin && (
            <MobileDrawerLink
              type="button"
              $active={pathActive(Routes.MANAGE_ACCESS)}
              onClick={() => navigateMobile(Routes.MANAGE_ACCESS)}
            >
              Manage Access
            </MobileDrawerLink>
          )}

          <MobileDrawerLink
            type="button"
            disabled={avatarUploading}
            onClick={onMobileUploadPhoto}
          >
            <UploadOutlined style={{ marginRight: 8 }} />
            {avatarUploading ? "Uploading..." : "Upload photo"}
          </MobileDrawerLink>

          <MobileDrawerLink type="button" onClick={onMobileLogout}>
            Logout
          </MobileDrawerLink>
        </nav>
      </Drawer>

      <OperatorHelpDrawer
        open={helpOpen}
        onClose={() => setHelpOpen(false)}
        viewerEmail={EmailAddress || ""}
      />

      <OperatorAdminChatDrawer
        open={adminChatOpen}
        onClose={() => {
          setAdminChatOpen(false);
          setAdminChatOperatorGameId(null);
          setAdminChatShowStartChatCta(false);
        }}
        viewerEmail={EmailAddress || ""}
        isSuperAdmin={isSuperAdmin}
        focusThreadKey={adminChatFocusKey}
        onConsumedFocus={() => setAdminChatFocusKey(null)}
        operatorOutboundGameId={adminChatOperatorGameId}
        showStartChatCta={adminChatShowStartChatCta}
        onActivity={() => {
          if (EmailAddress) {
            getAdminChatUnreadCount(EmailAddress).then(setChatUnread).catch(() => undefined);
            OperatorNotificationFeedInstance.refreshSnapshot(EmailAddress).catch(() => undefined);
          }
        }}
      />
    </>
  );
});
